import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { PaymentElement } from '@stripe/react-stripe-js'
import { AccountType, AppliedDiscountCodeType } from 'types'
import { SubmitButton } from 'lp-components'
import { AutoRenewalConsentField, TermsAgreementField } from 'member-portal/components'
import { useStripeActions } from 'utils'
import { createPaymentElementOptions } from 'utils/stripeOptions'

const propTypes = {
  account: AccountType,
  autoRenewalAgreement: PropTypes.bool,
  autoRenewalConsent: PropTypes.bool,
  discountCodeDetails: AppliedDiscountCodeType,
  handleSubmit: PropTypes.func.isRequired,
  isTrialMembershipsActive: PropTypes.bool,
  onError: PropTypes.func,
  onPaymentMethodCreated: PropTypes.func,
  promoCodeIsTrial: PropTypes.bool,
  shouldSubmitForm: PropTypes.bool,
  showTermsAndConditions: PropTypes.bool,
  submitting: PropTypes.bool,
  termsAgreement: PropTypes.bool,
}

function StripePaymentFormContent({
  account = null,
  autoRenewalAgreement = false,
  autoRenewalConsent = false,
  discountCodeDetails = null,
  handleSubmit,
  isTrialMembershipsActive = false,
  onError,
  onPaymentMethodCreated,
  promoCodeIsTrial = false,
  shouldSubmitForm = false,
  showTermsAndConditions = false,
  submitting = false,
  termsAgreement = false,
}) {
  const { isProcessing, createPaymentMethod } = useStripeActions()

  useEffect(() => {
    if (shouldSubmitForm) handleSubmit()
  }, [shouldSubmitForm, handleSubmit])

  const handleStripeSubmit = async (e) => {
    e.preventDefault()

    if (submitting || isProcessing) return

    if (showTermsAndConditions && !termsAgreement) {
      onError('You must agree to the terms and conditions to continue.')
      return
    }

    if (showTermsAndConditions && autoRenewalAgreement && !autoRenewalConsent) {
      onError('You must agree to the automatic renewal terms to continue.')
      return
    }

    try {
      const paymentMethodId = await createPaymentMethod()
      if (onPaymentMethodCreated) {
        onPaymentMethodCreated(paymentMethodId)
      }
    } catch (error) {
      onError(error.message)
    }
  }

  return (
    <form className="cc-details-block" onSubmit={handleStripeSubmit} noValidate>
      <>
        <label htmlFor="stripe-payment-element" className="form-label">
          Payment Information
          <span className="required-indicator">*</span>
        </label>
        <div id="stripe-payment-element">
          <PaymentElement options={createPaymentElementOptions({ account })} />
        </div>
      </>

      {showTermsAndConditions && (
        <>
          <hr className="divider" />

          <div className="consent-checkboxes">
            <TermsAgreementField />
            {autoRenewalAgreement && <AutoRenewalConsentField />}
          </div>
        </>
      )}

      {promoCodeIsTrial && isTrialMembershipsActive && (
        <div
          className="trial-legal-language"
          dangerouslySetInnerHTML={{
            __html: discountCodeDetails?.promo_legal,
          }}
        />
      )}

      <div className="group button-group">
        <SubmitButton submitting={submitting || isProcessing}>
          {submitting || isProcessing
            ? 'Processing Payment...'
            : 'Confirm Membership'}
        </SubmitButton>
      </div>
    </form>
  )
}

StripePaymentFormContent.propTypes = propTypes

export default StripePaymentFormContent
